import { Alert, Button } from '@mui/material';
import { useField, Field, useFormikContext } from 'formik';
import { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next'; 
import * as Yup from 'yup';

import { useAppDispatch } from '../../lib';
import { openSidebar } from '../../store';
import { Field as FormField, FieldType, Input } from '../../types/form-fields';
import { CheckboxField, FormRow, InputField as FormikInputField } from '../fields';
import { FormBlock } from './common';

export const isInputField = (field: FormField): field is Input => {
  return field.type === FieldType.Input;
};

export const defaultInputFieldProps: Input = {
  type: FieldType.Input,
  placeholder: 'Your Name',
  required: true,
  name: 'name',
};

type InputFieldSettingsProps = {
  name: string;
  remove?: () => void;
};

const textValueSchema = Yup.string().optional().max(255, 'Should be < 255 characters');

const SYSTEM_NAMES = ['name', 'first_name', 'last_name'] as const;

const InputFieldSettings: FC<InputFieldSettingsProps> = ({ name, remove }) => {
  const [, , helpers] = useField(`${name}.name`);
  const { submitForm } = useFormikContext();
  const { t } = useTranslation()

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  return (
    <InputFieldSettingsWrapper>
      <FormRow>
        <Field
          name={`${name}.placeholder`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.placeholder')}
          validate={(value: string) => validate(value, textValueSchema)}
        />
      </FormRow>

      <FormRow>
        <Field name={`${name}.required`} component={CheckboxField} label={t('pages.campaign.checkboxField.required')} />
      </FormRow>

      <FormRow>
        <Field
          name={`${name}.name`}
          component={FormikInputField}
          label={t('pages.campaign.checkboxField.name')}
          validate={(value: string) => validate(value, textValueSchema)}
        />

        <InputNameDescription severity="info" variant="filled">
        {t('pages.campaign.checkboxField.nameInputInfo')}
          <br />
          {SYSTEM_NAMES.map((systemName) => (
            <SystemName
              onClick={() => {
                helpers.setValue(systemName);
                submitForm();
              }}
            >
              {systemName}
            </SystemName>
          ))}
        </InputNameDescription>
      </FormRow>

      {remove && (
        <Button color="error" onClick={remove}>
          {t('pages.campaign.checkboxField.buttonRemove')}
        </Button>
      )}
    </InputFieldSettingsWrapper>
  );
};

type InputFieldProps = {
  name: string;
  remove?: () => void;
};

export const InputField: FC<InputFieldProps> = ({ name, remove }) => {
  const [field] = useField<Input>(name);
  const dispatch = useAppDispatch();

  const {
    value: { placeholder },
    ...restProps
  } = field;

  const handleClick = () => {
    dispatch(
      openSidebar({
        content: <InputFieldSettings name={name} remove={remove} />,
      })
    );
  };

  return (
    <FormBlock onClick={handleClick}>
      <Cover />

      <StyledInput {...restProps} placeholder={placeholder} />
    </FormBlock>
  );
};

const InputFieldSettingsWrapper = styled.div``;

const Cover = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  cursor: pointer;
`;

const StyledInput = styled.input`
  width: 100%;
  outline: none;
  border-radius: 5px;
  padding: 12px;
  height: 40px;
  font-size: 16px;
  border: 1px solid;
  color: #000;
  background-color: #fff;
  box-sizing: border-box;
`;

const InputNameDescription = styled(Alert)`
  padding: 4px 8px;
  margin-top: 4px;
  margin-bottom: 24px;
  max-width: 290px;
  font-size: 12px;
  font-weight: 400;

  .MuiAlert-icon {
    display: none;
  }

  .MuiAlert-message {
    padding: 0;
  }
`;

const SystemName = styled.div`
  display: inline-block;
  padding: 0 4px;
  color: #fff;
  font-weight: 500;
  background-color: #3899ec;
  margin-right: 4px;
  border-radius: 4px;
  cursor: pointer;

  :last-child {
    margin-right: 0;
  }
`;
